import React, { useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SuccessPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('booking');

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const {
    next7Days,
    selectedDays,
    mealSelections,
    menuOptions,
    resetOrder,
    matchedZone,
    cartItems,
    orderType,
    fulfillmentMode,
    scheduledTime,
    scheduledDate,
    globalDiscount,
    packagingFee,
    needCutlery,
  } = useOutletContext();

  const discountEnabled = globalDiscount?.enabled && globalDiscount?.rate && globalDiscount.rate < 1;
  const discountRate = discountEnabled ? globalDiscount.rate : 1;

  const isCartMode = cartItems?.length > 0;
  const hasWeeklyData = selectedDays?.length > 0;

  // No data (e.g. page refresh) → redirect to orders
  useEffect(() => {
    if (!isCartMode && !hasWeeklyData) {
      navigate('/orders', { replace: true });
    }
  }, [isCartMode, hasWeeklyData, navigate]);

  const handleComplete = () => {
    resetOrder();
    navigate('/orders');
  };

  if (!isCartMode && !hasWeeklyData) return null;

  // 計算訂單明細
  const calculateOrderBreakdown = () => {
    let originalSubtotal = 0;
    let subtotal = 0;
    let gst = 0;
    let pst = 0;

    if (isCartMode) {
      // Cart mode calculation with discount
      cartItems.forEach(item => {
        const modTotal = item.modifiers.reduce((s, m) => s + (m.price || 0), 0);
        const itemOriginal = (item.price + modTotal) * item.quantity;
        const itemTotal = itemOriginal * discountRate;
        originalSubtotal += itemOriginal;
        subtotal += itemTotal;
        if (item.tax_gst) gst += itemTotal * 0.05;
        if (item.tax_pst) pst += itemTotal * 0.07;
      });
    } else {
      // Weekly mode calculation
      selectedDays.forEach(dayId => {
        const daySelections = mealSelections[dayId] || {};
        ['lunch', 'dinner'].forEach(type => {
          const selection = daySelections[type];
          if (selection) {
            const mealId = typeof selection === 'object' ? selection.id : selection;
            const meal = menuOptions.find(m => m.id === mealId);
            if (meal) {
              let itemPrice = meal.price || 0;
              subtotal += itemPrice;
              if (meal.tax_gst) gst += itemPrice * 0.05;
              if (meal.tax_pst) pst += itemPrice * 0.07;
              if (selection.modifiers && Array.isArray(selection.modifiers)) {
                selection.modifiers.forEach(mod => {
                  const modPrice = mod.price || 0;
                  subtotal += modPrice;
                  if (meal.tax_gst) gst += modPrice * 0.05;
                  if (meal.tax_pst) pst += modPrice * 0.07;
                });
              }
            }
          }
        });
      });
    }

    let deliveryFee = 0;
    if (isCartMode && orderType === 'delivery' && matchedZone) {
      const rawFee = matchedZone.delivery_fee ?? matchedZone.deliveryFee ?? 0;
      deliveryFee = Number(rawFee);
    } else if (!isCartMode && matchedZone) {
      if (matchedZone.delivery_fee !== undefined && matchedZone.delivery_fee !== null) {
        deliveryFee = Number(matchedZone.delivery_fee);
      } else if (matchedZone.deliveryFee !== undefined && matchedZone.deliveryFee !== null) {
        deliveryFee = Number(matchedZone.deliveryFee);
      }
    }

    const discount = discountEnabled ? originalSubtotal - subtotal : 0;
    const pkgFee = packagingFee || 0;

    return {
      originalSubtotal: discountEnabled ? originalSubtotal : subtotal,
      subtotal,
      discount,
      gst,
      pst,
      packagingFee: pkgFee,
      deliveryFee,
      grandTotal: subtotal + gst + pst + pkgFee + deliveryFee
    };
  };

  const orderBreakdown = calculateOrderBreakdown();

  return (
    <div className="success-page" style={styles.successPage}>
      <style>{`
        .success-complete-btn:hover {
          background: #245F48 !important;
          transform: translateY(-2px);
        }
        @media (max-width: 640px) {
          .success-page {
            padding: 24px 10px !important;
            min-height: calc(100vh - 60px) !important;
            align-items: flex-start !important;
          }
          .success-content {
            padding: 28px 16px !important;
            border-radius: 4px !important;
          }
          .success-icon-lg {
            width: 68px !important;
            height: 68px !important;
            margin-bottom: 18px !important;
          }
          .success-icon-lg svg {
            width: 36px !important;
            height: 36px !important;
          }
          .success-title {
            font-size: 1.5rem !important;
            margin-bottom: 8px !important;
          }
          .success-subtitle {
            font-size: 0.95rem !important;
            margin-bottom: 24px !important;
          }
          .success-order-details {
            padding: 16px !important;
            border-radius: 4px !important;
            margin-bottom: 24px !important;
          }
          .success-order-details h3 {
            font-size: 0.8rem !important;
            margin-bottom: 16px !important;
          }
          .success-meal-row {
            gap: 4px !important;
          }
          .success-meal-name {
            font-size: 0.9rem !important;
            gap: 6px !important;
          }
          .success-meal-price {
            font-size: 0.9rem !important;
          }
          .success-mods {
            padding-left: 0 !important;
          }
          .success-total-section {
            padding-top: 16px !important;
            margin-top: 16px !important;
          }
          .success-grand-total {
            font-size: 1.2rem !important;
            padding-top: 16px !important;
            margin-top: 16px !important;
          }
          .success-complete-btn {
            padding: 14px 20px !important;
            font-size: 1rem !important;
          }
        }
      `}</style>
      <div className="success-content" style={styles.successContent}>
        <div className="success-icon-lg" style={styles.successIconLarge}>
          <Check size={60} />
        </div>

        <h1 className="success-title" style={styles.successTitle}>{t('success.title')}</h1>
        <p className="success-subtitle" style={styles.successSubtitle}>{t('success.subtitle')}</p>

        <div className="success-order-details" style={styles.successOrderDetails}>
          <h3 style={styles.orderDetailsTitle}>{t('success.order_details')}</h3>

          {/* Cart mode: order type & fulfillment info */}
          {isCartMode && (orderType || fulfillmentMode) && (
            <div style={styles.orderTypeSection}>
              {orderType && (
                <div style={styles.orderTypeRow}>
                  <span style={styles.orderTypeLabel}>{t('success.pickup_method', '取餐方式')}</span>
                  <span style={styles.orderTypeValue}>
                    {orderType === 'pickup' ? t('date_selection.pickup_label', '自取') : t('success.delivery', '配送')}
                  </span>
                </div>
              )}
              {fulfillmentMode && (
                <div style={styles.orderTypeRow}>
                  <span style={styles.orderTypeLabel}>{t('success.pickup_type', '取餐類型')}</span>
                  <span style={styles.orderTypeValue}>
                    {fulfillmentMode === 'instant'
                      ? (orderType === 'pickup' ? t('success.instant_pickup', '立即取餐') : t('success.instant_delivery', '立即配送'))
                      : t('success.scheduled_pickup', '預約時間取餐')}
                  </span>
                </div>
              )}
              {fulfillmentMode === 'scheduled' && scheduledTime && (
                <div style={styles.orderTypeRow}>
                  <span style={styles.orderTypeLabel}>{orderType === 'pickup' ? t('success.scheduled_pickup_time', '預約取餐時間') : t('success.scheduled_delivery_time', '預約配送時間')}</span>
                  <span style={styles.orderTypeValue}>
                    {scheduledDate && scheduledTime
                      ? new Date(`${scheduledDate}T${scheduledTime}`).toLocaleString(i18n.language === 'en' ? 'en-CA' : 'zh-TW', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
                      : scheduledTime || '-'}
                  </span>
                </div>
              )}
              {needCutlery !== undefined && (
                <div style={styles.orderTypeRow}>
                  <span style={styles.orderTypeLabel}>{t('payment.cutlery', '是否需要餐具')}</span>
                  <span style={{ ...styles.orderTypeValue, color: needCutlery ? '#2F7D5E' : '#ef4444', fontWeight: 500 }}>
                    {needCutlery ? t('payment.cutlery_yes', '需要') : t('payment.cutlery_no', '不需要')}
                  </span>
                </div>
              )}
            </div>
          )}

          <div style={styles.successItems}>
            {/* Cart mode items */}
            {isCartMode && cartItems.map((item, index) => {
              const modTotal = item.modifiers.reduce((s, m) => s + (m.price || 0), 0);
              const lineOriginal = (item.price + modTotal) * item.quantity;
              const lineTotal = lineOriginal * discountRate;
              const isLast = index === cartItems.length - 1;
              return (
                <div key={item.cartId} style={{...styles.successItem, ...(isLast && styles.successItemLast)}}>
                  <div style={styles.cartItemRow}>
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} style={styles.cartItemImg} loading="lazy" decoding="async" />
                    ) : (
                      <div style={{...styles.cartItemImg, display:'flex', alignItems:'center', justifyContent:'center', background:'#f1f5f9', color:'#ccc', fontSize:'1.2rem'}}>
                        {item.emoji || '🍽'}
                      </div>
                    )}
                    <div style={styles.cartItemInfo}>
                      <div className="success-meal-row" style={styles.successMeal}>
                        <span className="success-meal-name" style={styles.successMealName}>
                          {item.name}
                        </span>
                        <span className="success-meal-price" style={styles.successMealPrice}>
                          {discountEnabled && <span style={{textDecoration:'line-through', color:'#999', fontSize:'0.85em', marginRight:4}}>CAD$ {lineOriginal.toFixed(2)}</span>}
                          CAD$ {lineTotal.toFixed(2)}
                        </span>
                      </div>
                      {item.quantity > 1 && (
                        <div style={{fontSize: '0.8rem', color: '#94a3b8'}}>x{item.quantity} @ CAD$ {item.price.toFixed(2)}</div>
                      )}
                      {item.modifiers.length > 0 && (
                        <div className="success-mods" style={styles.cartModsList}>
                          {item.modifiers.map((mod, i) => (
                            <div key={i} style={styles.modifierItem}>
                              <span>+ {mod.name}</span>
                              {mod.price > 0 && (
                                discountEnabled
                                  ? <span style={styles.modifierPrice}> (<s>${mod.price.toFixed(2)}</s> ${(mod.price * discountRate).toFixed(2)})</span>
                                  : <span style={styles.modifierPrice}>(+${mod.price.toFixed(2)})</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {item.note && (
                        <div style={{fontSize: '0.8rem', color: '#b45309', fontStyle: 'italic', marginTop: '2px'}}>
                          {t('single_meal.note_label', '備註')}: {item.note}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Weekly mode items */}
            {!isCartMode && selectedDays?.map((dayId, index) => {
              const day = next7Days.find(d => d.id === dayId);
              const daySelection = mealSelections[dayId] || {};

              const lunchSelection = daySelection.lunch;
              const dinnerSelection = daySelection.dinner;

              const lunchId = typeof lunchSelection === 'object' ? lunchSelection.id : lunchSelection;
              const dinnerId = typeof dinnerSelection === 'object' ? dinnerSelection.id : dinnerSelection;

              const lunchMeal = lunchId ? menuOptions.find(m => m.id === lunchId) : null;
              const dinnerMeal = dinnerId ? menuOptions.find(m => m.id === dinnerId) : null;

              if (!lunchMeal && !dinnerMeal) return null;

              const isLastItem = index === selectedDays.length - 1;
              const itemStyle = {
                ...styles.successItem,
                ...(isLastItem && styles.successItemLast)
              };

              const calculateMealTotal = (meal, selection) => {
                if (!meal) return 0;
                let total = meal.price || 0;
                if (selection && selection.modifiers && Array.isArray(selection.modifiers)) {
                  selection.modifiers.forEach(mod => {
                    total += mod.price || 0;
                  });
                }
                return total;
              };

              return (
                <div key={dayId} style={itemStyle}>
                  <div style={styles.successDate}>
                    {day.fullDate} {day.weekday}
                  </div>

                  {/* --- 午餐區塊 --- */}
                  {lunchMeal && (
                    <div style={styles.mealRowContainer}>
                      <div className="success-meal-row" style={styles.successMeal}>
                        <span className="success-meal-name" style={styles.successMealName}>
                          <span style={styles.mealTag}>{t('success.lunch_tag')}</span>
                          <span>{lunchMeal.emoji}</span>
                          {lunchMeal.name}
                        </span>
                        <span className="success-meal-price" style={styles.successMealPrice}>
                          CAD$ {calculateMealTotal(lunchMeal, lunchSelection).toFixed(2)}
                        </span>
                      </div>

                      {/* 🔥 修改：午餐 modifiers 顯示方式 */}
                      {lunchSelection && lunchSelection.modifiers && lunchSelection.modifiers.length > 0 && (
                        <div className="success-mods" style={styles.modifiersList}>
                          {lunchSelection.modifiers.map((mod, i) => {
                             const price = Number(mod.price || 0);
                             return (
                               <div key={i} style={styles.modifierItem}>
                                 <span>+ {mod.name}</span>
                                 {price > 0 && (
                                   <span style={styles.modifierPrice}>
                                     (+${price.toFixed(2)})
                                   </span>
                                 )}
                               </div>
                             );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {/* --- 晚餐區塊 --- */}
                  {dinnerMeal && (
                    <div style={{...styles.mealRowContainer, marginTop: lunchMeal ? '12px' : '0'}}>
                      <div className="success-meal-row" style={styles.successMeal}>
                        <span className="success-meal-name" style={styles.successMealName}>
                          <span style={styles.mealTag}>{t('success.dinner_tag')}</span>
                          <span>{dinnerMeal.emoji}</span>
                          {dinnerMeal.name}
                        </span>
                        <span className="success-meal-price" style={styles.successMealPrice}>
                          CAD$ {calculateMealTotal(dinnerMeal, dinnerSelection).toFixed(2)}
                        </span>
                      </div>

                      {/* 🔥 修改：晚餐 modifiers 顯示方式 */}
                      {dinnerSelection && dinnerSelection.modifiers && dinnerSelection.modifiers.length > 0 && (
                        <div className="success-mods" style={styles.modifiersList}>
                          {dinnerSelection.modifiers.map((mod, i) => {
                             const price = Number(mod.price || 0);
                             return (
                               <div key={i} style={styles.modifierItem}>
                                 <span>+ {mod.name}</span>
                                 {price > 0 && (
                                   <span style={styles.modifierPrice}>
                                     (+${price.toFixed(2)})
                                   </span>
                                 )}
                               </div>
                             );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="success-total-section" style={styles.successTotalSection}>
            {isCartMode && discountEnabled ? (
              <>
                <div style={styles.successBillRow}>
                  <span>{t('success.subtotal')}</span>
                  <span style={{textDecoration:'line-through', color:'#999'}}>CAD$ {orderBreakdown.originalSubtotal.toFixed(2)}</span>
                </div>
                <div style={{...styles.successBillRow, color:'#e53e3e'}}>
                  <span>{globalDiscount.label || t('single_meal.discount', '全單折扣')}</span>
                  <span>-CAD$ {orderBreakdown.discount.toFixed(2)}</span>
                </div>
                <div style={styles.successBillRow}>
                  <span>{t('meal_selection.discounted_subtotal', '折後小計')}</span>
                  <span>CAD$ {orderBreakdown.subtotal.toFixed(2)}</span>
                </div>
              </>
            ) : (
              <div style={styles.successBillRow}>
                <span>{t('success.subtotal')}</span>
                <span>CAD$ {orderBreakdown.subtotal.toFixed(2)}</span>
              </div>
            )}
            {(!isCartMode || orderType === 'delivery') && (
            <div style={{...styles.successBillRow, ...styles.feeRow}}>
              <span>{t('success.delivery_fee')}</span>
              <span style={{color: orderBreakdown.deliveryFee === 0 ? '#166534' : 'inherit'}}>
                {orderBreakdown.deliveryFee === 0 ? t('success.free') : `CAD$ ${orderBreakdown.deliveryFee.toFixed(2)}`}
              </span>
            </div>
            )}
            {orderBreakdown.packagingFee > 0 && (
            <div style={styles.successBillRow}>
              <span>{t('payment.packaging_fee', '外賣包裝費')}</span>
              <span>CAD$ {orderBreakdown.packagingFee.toFixed(2)}</span>
            </div>
            )}
            <div style={{...styles.successBillRow, ...styles.taxRow}}>
              <span>GST (5%)</span>
              <span>CAD$ {orderBreakdown.gst.toFixed(2)}</span>
            </div>
            <div style={{...styles.successBillRow, ...styles.taxRow}}>
              <span>PST (7%)</span>
              <span>CAD$ {orderBreakdown.pst.toFixed(2)}</span>
            </div>
            <div className="success-grand-total" style={styles.successTotal}>
              <span>{t('success.total')}</span>
              <span>CAD$ {orderBreakdown.grandTotal.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <button
          onClick={handleComplete}
          className="success-complete-btn"
          style={{ ...styles.btn, ...styles.btnPrimary, ...styles.btnLarge }}
        >
          {t('success.go_orders')}
        </button>
      </div>
    </div>
  );
};

// 樣式定義
const styles = {
  successPage: {
    minHeight: 'calc(100vh - 80px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    background: '#fff',
  },
  successContent: {
    background: '#fff',
    borderRadius: '4px',
    padding: '60px 50px',
    maxWidth: '700px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  },
  successIconLarge: {
    width: '100px',
    height: '100px',
    background: '#2F7D5E',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 30px',
    color: '#fff',
    boxShadow: '0 8px 25px rgba(47,125,94,0.2)',
  },
  successTitle: {
    fontSize: '2.5rem',
    fontWeight: '400',
    color: '#1a1a1a',
    marginBottom: '15px',
    letterSpacing: '-0.5px',
  },
  successSubtitle: {
    fontSize: '1.1rem',
    color: '#666',
    marginBottom: '50px',
    fontWeight: '400',
  },
  successOrderDetails: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    padding: '35px',
    marginBottom: '40px',
    textAlign: 'left',
  },
  orderDetailsTitle: {
    fontSize: '0.9rem',
    fontWeight: '400',
    color: '#1a1a1a',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '25px',
    textAlign: 'center',
  },
  orderTypeSection: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    padding: '14px 18px',
    marginBottom: '20px',
  },
  orderTypeRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  orderTypeLabel: {
    fontSize: '0.88rem',
    color: '#94a3b8',
    fontWeight: '400',
  },
  orderTypeValue: {
    fontSize: '0.92rem',
    fontWeight: '400',
    color: '#1e293b',
  },
  successItems: {
    marginBottom: '25px',
  },
  cartItemRow: {
    display: 'grid',
    gridTemplateColumns: '50px 1fr',
    gap: '12px',
    alignItems: 'start',
  },
  cartItemImg: {
    width: '50px',
    height: '50px',
    borderRadius: '4px',
    objectFit: 'cover',
    border: '1px solid #e2e8f0',
  },
  cartItemInfo: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  cartModsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    marginTop: '2px',
  },
  successItem: {
    padding: '18px 0',
    borderBottom: '1px solid #e0e0e0',
  },
  successItemLast: {
    borderBottom: 'none',
  },
  successDate: {
    fontSize: '0.8rem',
    color: '#999',
    marginBottom: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    fontWeight: '400',
  },
  // 新增容器樣式
  mealRowContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  successMeal: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  successMealName: {
    fontWeight: '400',
    color: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '1rem',
    flexWrap: 'wrap',
  },
  mealTag: {
    fontSize: '0.75rem',
    padding: '2px 6px',
    borderRadius: '4px',
    background: '#e0e0e0',
    color: '#555',
    fontWeight: '400',
  },
  // 🔥 新增：Modifiers 列表樣式
  modifiersList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    paddingLeft: '50px', // 讓它縮排，對齊餐點名稱下方
  },
  // 🔥 新增：個別 Modifier 項目樣式
  modifierItem: {
    fontSize: '0.85rem',
    color: '#666',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  // 🔥 新增：Modifier 價格樣式
  modifierPrice: {
    color: '#e11d48', // 稍微紅一點凸顯價格
    fontWeight: '400',
    fontSize: '0.8rem',
  },
  successMealPrice: {
    fontWeight: '400',
    color: '#111',
    fontSize: '1.05rem',
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap',
  },
  successTotalSection: {
    borderTop: '1px solid #e0e0e0',
    paddingTop: '25px',
    marginTop: '25px',
  },
  successBillRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '8px',
    fontSize: '0.95rem',
    color: '#666',
  },
  feeRow: {
    marginBottom: '12px',
    paddingBottom: '12px',
    borderBottom: '1px dashed #e0e0e0',
  },
  taxRow: {
    color: '#888',
    fontSize: '0.85rem',
  },
  successTotal: {
    paddingTop: '25px',
    marginTop: '25px',
    borderTop: '1px solid #ccc',
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '1.6rem',
    fontWeight: '400',
    color: '#1a1a1a',
    letterSpacing: '-0.5px',
  },
  btn: {
    padding: '16px 40px',
    border: 'none',
    fontSize: '0.95rem',
    fontWeight: '400',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    letterSpacing: '0.3px',
    borderRadius: '4px',
  },
  btnPrimary: {
    background: '#2F7D5E',
    color: '#fff',
    boxShadow: '0 4px 12px rgba(47,125,94,0.2)',
  },
  btnLarge: {
    padding: '18px 60px',
    fontSize: '1.1rem',
    width: '100%',
  },
};

export default SuccessPage;
